import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import BrandButton from "../../../components/v2/Buttons/BrandButton";
import {
  businessType,
  taxClassificationTypes,
} from "../../../constants/typeOfEntity";
import {
  ACTIONTYPES,
  useStore,
  useStoreDispatch,
} from "../../../context/CreateStoreContext";
import useComponentSize from "../../../hooks/useComponentSize";

const { Title, Paragraph } = Typography;

export default function BusinessInfo({ proceed, back }) {
  const componentSize = useComponentSize();
  const dispatch = useStoreDispatch();
  const { business } = useStore();
  const [form] = Form.useForm();
  const submitButtonRef = useRef();
  const watchTypeOfEntity = Form.useWatch("typeOfEntity", form);

  const onFinish = (values) => {
    const { legalDOB, ein, ssn } = values;

    const formattedlegalDOB = legalDOB
      ? legalDOB.format("YYYY-MM-DD")
      : undefined;

    dispatch({
      type: ACTIONTYPES.BUSINESS,
      data: {
        ...values,
        ssn: !ssn ? undefined : ssn,
        ein: !ein ? undefined : ein,
        legalDOB: formattedlegalDOB,
      },
    });
    proceed();
  };

  useEffect(() => {
    form.setFieldsValue({
      ...business,
      legalDOB:
        business?.legalDOB && dayjs(business?.legalDOB).isValid()
          ? dayjs(business.legalDOB)
          : null,
    });
  }, [form]);

  return (
    <Row className="BevzForm">
      <Col
        xs={{ span: 22, offset: 1 }}
        lg={{ span: 20, offset: 2 }}
        xl={{ span: 15, offset: 2 }}
      >
        <Title
          level={componentSize === "default" ? 4 : 2}
          className="BevzFormHeader"
        >
          Business Information
        </Title>
        <Paragraph className="BevzFormSubHeader">
          Make sure to provide correct details about your business.
        </Paragraph>

        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          style={{ marginTop: "2.5rem", width: "100%" }}
          autoComplete="off"
        >
          <Row gutter={30}>
            <Col xs={24} md={12}>
              <Form.Item name="typeOfEntity" label="Business Type">
                <Select
                  options={businessType}
                  placeholder="Select Business Type"
                  size="large"
                />
              </Form.Item>
            </Col>

            {watchTypeOfEntity === businessType[4].value ? (
              <Col span={12}>
                <Form.Item
                  name="taxClassification"
                  label="Tax Classification"
                  required
                  rules={[
                    { required: true, message: "This is a required field." },
                  ]}
                >
                  <Select
                    options={taxClassificationTypes}
                    placeholder="Select Business Type"
                    size="large"
                  />
                </Form.Item>
              </Col>
            ) : null}

            <Col xs={24} md={12}>
              <Form.Item
                name="ssn"
                label="Last 4 Digits of Owner's SSN"
                rules={[
                  {
                    min: 4,
                    message: "Please enter last 4 digits of your SSN.",
                  },
                ]}
              >
                <Input.Password
                  maxLength={4}
                  placeholder=""
                  size="large"
                  onChange={(e) =>
                    form.setFieldValue("ssn", e.target.value.replace(/\D/g, ""))
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="ein"
                label="Employer ID Number"
                rules={[
                  {
                    min: 9,
                    message: "Please enter 9-digit Employer ID Number (EIN).",
                  },
                  {
                    max: 9,
                    message: "Please enter 9-digit Employer ID Number (EIN).",
                  },
                ]}
                normalize={(value) => {
                  if (!value) return value;
                  return value.replace(/\D/g, "");
                }}
              >
                <Input.Password placeholder="" size="large" maxLength={9} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="legalDOB"
                label="Owner’s Date of Birth"
                required={false}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value && value.isSame(dayjs(), "day")) {
                        return Promise.reject(
                          new Error("Please enter valid birth date."),
                        );
                      }

                      if (
                        value &&
                        value.isAfter(dayjs().subtract(16, "years"))
                      ) {
                        return Promise.reject(
                          new Error("Must be at least 16 years old."),
                        );
                      }

                      if (
                        value &&
                        value.isBefore(dayjs().subtract(100, "years"))
                      ) {
                        return Promise.reject(
                          new Error("Must be less than 100 years old."),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <DatePicker
                  size="large"
                  format="MM/DD/YYYY"
                  style={{ width: "100%" }}
                  maxDate={dayjs(dayjs(), "MM/DD/YYYY")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row
            align="middle"
            justify="space-between"
            className="BevzFormButtonSection"
          >
            <Col>
              <Form.Item>
                <Button
                  size="large"
                  type="default"
                  shape="round"
                  icon={<ArrowLeftOutlined />}
                  onClick={back}
                >
                  Back
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item>
                <BrandButton
                  type="solidyellow"
                  ref={submitButtonRef}
                  htmlType="submit"
                  size="large"
                >
                  {componentSize === "default"
                    ? "Proceed"
                    : "Proceed to Store Hours"}
                </BrandButton>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
}
